import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { getUser } from '../utils/auth';
import { 
  Clock, 
  AlertCircle,
  CheckCircle,
  Send,
  Loader2,
  ChevronLeft,
  ChevronRight,
  Save
} from 'lucide-react';
import { toast } from 'sonner';

const LTIExamTake = () => {
  const { examId } = useParams();
  const [searchParams] = useSearchParams();
  const launchId = searchParams.get('launch_id');
  const navigate = useNavigate();
  const user = getUser();

  const [exam, setExam] = useState(null);
  const [answers, setAnswers] = useState({});
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [timeLeft, setTimeLeft] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    fetchExam();
  }, [examId]);

  useEffect(() => {
    if (timeLeft === null || timeLeft <= 0 || submitted) return;

    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          handleAutoSubmit();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [timeLeft, submitted]);

  const fetchExam = async () => {
    try {
      const response = await api.get(`/exams/${examId}`);
      setExam(response.data);
      setTimeLeft((response.data.duration || 60) * 60);
      
      // Initialize answers
      const initialAnswers = {};
      response.data.questions?.forEach(q => {
        initialAnswers[q.id] = q.type === 'structured' ? {} : '';
      });
      setAnswers(initialAnswers);
    } catch (err) {
      toast.error('Failed to load exam');
    } finally {
      setLoading(false);
    }
  };

  const handleAnswerChange = (questionId, value, partKey = null) => {
    setAnswers(prev => {
      if (partKey) {
        return {
          ...prev,
          [questionId]: {
            ...prev[questionId],
            [partKey]: value
          }
        };
      }
      return { ...prev, [questionId]: value };
    });
  };

  const handleAutoSubmit = async () => {
    toast.warning('Time is up! Submitting your answers...');
    await handleSubmit();
  };

  const handleSubmit = async () => {
    if (submitting) return;
    setSubmitting(true);

    try {
      // Format answers for submission - must match backend Answer model
      const formattedAnswers = [];
      
      exam.questions.forEach((q, qIdx) => {
        const answer = answers[q.id];
        
        if (q.type === 'mcq') {
          // MCQ - find selected option index
          const optionIdx = q.options?.indexOf(answer);
          formattedAnswers.push({
            question_idx: qIdx,
            part_idx: -1,
            sub_part_idx: -1,
            selected_option: optionIdx >= 0 ? optionIdx : null,
            text_answer: null
          });
        } else if (q.parts && q.parts.length > 0 && typeof answer === 'object') {
          // Structured question with parts
          q.parts.forEach((part, partIdx) => {
            if (part.sub_parts && part.sub_parts.length > 0) {
              // Has sub-parts
              part.sub_parts.forEach((subPart, subPartIdx) => {
                const key = `${part.id || partIdx}_${subPart.id || subPartIdx}`;
                formattedAnswers.push({
                  question_idx: qIdx,
                  part_idx: partIdx,
                  sub_part_idx: subPartIdx,
                  text_answer: answer?.[key] || '',
                  selected_option: null
                });
              });
            } else {
              // No sub-parts
              const key = part.id || part.part_label || partIdx;
              formattedAnswers.push({
                question_idx: qIdx,
                part_idx: partIdx,
                sub_part_idx: -1,
                text_answer: answer?.[key] || '',
                selected_option: null
              });
            }
          });
        } else {
          // Simple text answer
          formattedAnswers.push({
            question_idx: qIdx,
            part_idx: -1,
            sub_part_idx: -1,
            text_answer: typeof answer === 'string' ? answer : '',
            selected_option: null
          });
        }
      });

      const response = await api.post('/submissions', {
        exam_id: examId,
        answers: formattedAnswers,
        timer_remaining: timeLeft || 0
      });

      setSubmitted(true);
      setResult(response.data);

      // If we have AGS, grades will be sent automatically
      toast.success('Exam submitted successfully!');

      // Try to send grade back to Moodle if launch supports it
      if (launchId && response.data.mcq_score !== undefined) {
        try {
          const score = response.data.mcq_score || 0;
          const totalMarks = response.data.mcq_total || response.data.max_marks || 100;
          
          await api.post('/lti/grades/submit', {
            launch_id: launchId,
            user_id: user?.id,
            score: totalMarks > 0 ? score / totalMarks : 0,
            max_score: totalMarks,
            comment: `Submitted at ${new Date().toLocaleString()}`
          });
          toast.success('Grade synced to Moodle');
        } catch (gradeErr) {
          // Grade passback might not be configured
          console.log('Grade passback not available:', gradeErr);
        }
      }
    } catch (err) {
      console.error('Submission error:', err);
      toast.error(err.response?.data?.detail || 'Failed to submit exam');
    } finally {
      setSubmitting(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="h-full min-h-[400px] bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  if (!exam) {
    return (
      <div className="h-full min-h-[400px] bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 mx-auto mb-4 text-red-500" />
          <p className="text-slate-700">Exam not found</p>
        </div>
      </div>
    );
  }

  if (submitted && result) {
    // Calculate score from MCQ or marks
    const score = result.mcq_score ?? result.marks_allocated ?? 0;
    const totalMarks = result.mcq_total ?? result.max_marks ?? 100;
    const percentage = totalMarks > 0 ? Math.round((score / totalMarks) * 100) : 0;
    
    return (
      <div className="h-full min-h-[400px] bg-slate-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center">
          <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-emerald-600" />
          </div>
          <h2 className="text-2xl font-bold text-slate-900 mb-2">Exam Submitted!</h2>
          <p className="text-slate-600 mb-6">Your answers have been recorded.</p>
          
          {result.is_mcq_auto_graded ? (
            <div className="bg-slate-50 rounded-xl p-4 mb-6">
              <p className="text-sm text-slate-500 mb-1">Your Score</p>
              <p className="text-3xl font-bold text-indigo-600">
                {score} / {totalMarks}
              </p>
              <p className="text-sm text-slate-500 mt-1">
                ({percentage}%)
              </p>
            </div>
          ) : (
            <div className="bg-amber-50 rounded-xl p-4 mb-6">
              <p className="text-sm text-amber-700">
                Your submission will be graded by your teacher.
              </p>
            </div>
          )}

          <p className="text-sm text-slate-500">
            You can close this window and return to Moodle.
          </p>
        </div>
      </div>
    );
  }

  const currentQ = exam.questions?.[currentQuestion];

  return (
    <div className="bg-slate-50 overflow-auto" style={{ height: '100vh', maxHeight: '100%' }}>
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-4 py-3 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="font-semibold text-slate-900 text-lg">{exam.title}</h1>
            <p className="text-sm text-slate-500">
              Question {currentQuestion + 1} of {exam.questions?.length || 0}
            </p>
          </div>
          <div className={`flex items-center gap-2 px-4 py-2 rounded-lg ${
            timeLeft < 300 ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-700'
          }`}>
            <Clock className="w-5 h-5" />
            <span className="font-mono font-medium">{formatTime(timeLeft)}</span>
          </div>
        </div>
      </div>

      {/* Question */}
      <div className="flex-1 p-4">
        <div className="max-w-4xl mx-auto">
          {currentQ && (
            <div className="bg-white rounded-xl shadow-sm p-6 mb-4">
              <div className="flex items-start gap-4 mb-4">
                <span className="px-3 py-1 bg-indigo-100 text-indigo-700 rounded-lg font-medium text-sm">
                  Q{currentQuestion + 1}
                </span>
                <span className="px-3 py-1 bg-slate-100 text-slate-600 rounded-lg text-sm">
                  {currentQ.marks || 1} marks
                </span>
              </div>

              <div className="mb-6">
                <p className="text-slate-900 text-lg whitespace-pre-wrap">{currentQ.text || currentQ.question_text}</p>
              </div>

              {/* MCQ Options */}
              {currentQ.type === 'mcq' && currentQ.options && (
                <div className="space-y-3">
                  {currentQ.options.map((option, idx) => (
                    <label
                      key={idx}
                      className={`flex items-center gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all ${
                        answers[currentQ.id] === option
                          ? 'border-indigo-500 bg-indigo-50'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <input
                        type="radio"
                        name={`question-${currentQ.id}`}
                        value={option}
                        checked={answers[currentQ.id] === option}
                        onChange={() => handleAnswerChange(currentQ.id, option)}
                        className="sr-only"
                      />
                      <span className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                        answers[currentQ.id] === option
                          ? 'border-indigo-500 bg-indigo-500'
                          : 'border-slate-300'
                      }`}>
                        {answers[currentQ.id] === option && (
                          <CheckCircle className="w-4 h-4 text-white" />
                        )}
                      </span>
                      <span className="text-slate-700">{option}</span>
                    </label>
                  ))}
                </div>
              )}

              {/* Structured/Descriptive Question with Parts */}
              {currentQ.parts && currentQ.parts.length > 0 && (
                <div className="space-y-6">
                  {currentQ.parts.map((part, idx) => (
                    <div key={part.id || part.part_label || idx} className="border-l-4 border-indigo-200 pl-4">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-sm font-medium text-indigo-600">
                          ({part.part_label || String.fromCharCode(97 + idx)})
                        </span>
                        {part.marks && (
                          <span className="text-sm text-slate-500">[{part.marks} marks]</span>
                        )}
                      </div>
                      <p className="text-slate-700 mb-3">{part.text || part.part_text}</p>
                      
                      {part.sub_parts && part.sub_parts.length > 0 ? (
                        <div className="space-y-4 ml-4">
                          {part.sub_parts.map((subPart, subIdx) => (
                            <div key={subPart.id || subIdx}>
                              <div className="flex items-center gap-2 mb-2">
                                <span className="text-sm text-slate-500">
                                  ({['i', 'ii', 'iii', 'iv', 'v'][subIdx]})
                                </span>
                                {subPart.marks && (
                                  <span className="text-sm text-slate-400">[{subPart.marks} marks]</span>
                                )}
                              </div>
                              <p className="text-slate-600 mb-2 text-sm">{subPart.text}</p>
                              <textarea
                                value={answers[currentQ.id]?.[`${part.id || idx}_${subPart.id || subIdx}`] || ''}
                                onChange={(e) => handleAnswerChange(
                                  currentQ.id, 
                                  e.target.value,
                                  `${part.id || idx}_${subPart.id || subIdx}`
                                )}
                                rows={3}
                                className="w-full p-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                                placeholder="Enter your answer..."
                              />
                            </div>
                          ))}
                        </div>
                      ) : (
                        <textarea
                          value={answers[currentQ.id]?.[part.id || part.part_label || idx] || ''}
                          onChange={(e) => handleAnswerChange(currentQ.id, e.target.value, part.id || part.part_label || idx)}
                          rows={4}
                          className="w-full p-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          placeholder="Enter your answer..."
                        />
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Simple Text Answer - for questions without parts */}
              {(!currentQ.parts || currentQ.parts.length === 0) && currentQ.type !== 'mcq' && (
                <textarea
                  value={answers[currentQ.id] || ''}
                  onChange={(e) => handleAnswerChange(currentQ.id, e.target.value)}
                  rows={6}
                  className="w-full p-4 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="Enter your answer..."
                />
              )}
            </div>
          )}
        </div>
      </div>

      {/* Navigation - Fixed at bottom */}
      <div className="bg-white border-t border-slate-200 px-4 py-4 sticky bottom-0 z-10">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <button
            onClick={() => setCurrentQuestion(prev => Math.max(0, prev - 1))}
            disabled={currentQuestion === 0}
            className="flex items-center gap-2 px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronLeft className="w-5 h-5" />
            Previous
          </button>

          {/* Question dots */}
          <div className="flex items-center gap-1 overflow-x-auto max-w-md">
            {exam.questions?.map((q, idx) => (
              <button
                key={q.id}
                onClick={() => setCurrentQuestion(idx)}
                className={`w-8 h-8 rounded-full text-sm font-medium transition-colors flex-shrink-0 ${
                  idx === currentQuestion
                    ? 'bg-indigo-600 text-white'
                    : answers[q.id] && (typeof answers[q.id] === 'string' ? answers[q.id].trim() : Object.keys(answers[q.id]).length > 0)
                      ? 'bg-emerald-100 text-emerald-700'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                {idx + 1}
              </button>
            ))}
          </div>

          {currentQuestion === (exam.questions?.length || 0) - 1 ? (
            <button
              onClick={handleSubmit}
              disabled={submitting}
              className="flex items-center gap-2 px-6 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 transition-colors"
            >
              {submitting ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Send className="w-5 h-5" />
              )}
              Submit
            </button>
          ) : (
            <button
              onClick={() => setCurrentQuestion(prev => Math.min((exam.questions?.length || 1) - 1, prev + 1))}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
            >
              Next
              <ChevronRight className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default LTIExamTake;
